import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Upload, X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface CampaignSummary {
  id: string;
  name: string;
}

interface ImportSession {
  id: string;
  campaignId: string | null;
  filename: string;
  version: number;
  status: string;
  rowCount: number | null;
  llmProvider: string | null;
  llmModel: string | null;
  createdAt: string | null;
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  MAPPING: 'bg-amber-900/40 text-amber-200',
  EXTRACTING: 'bg-amber-900/40 text-amber-200',
  APPLYING: 'bg-amber-900/40 text-amber-200',
  COMPLETED: 'bg-emerald-900/40 text-emerald-200',
  FAILED: 'bg-red-900/40 text-red-200',
};

/**
 * Story 49.5 — list page for AI-driven Excel imports.
 */
export function ImportsPage() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<ImportSession[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  function load() {
    Promise.all([
      apiClient.get<ImportSession[]>('/imports'),
      apiClient.get<CampaignSummary[]>('/campaigns'),
    ])
      .then(([s, c]) => {
        setSessions(s.data);
        setCampaigns(c.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
  }, []);

  if (loading) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('imports.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('imports.subtitle')}</p>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Upload className="mr-1 h-4 w-4" /> {t('imports.new_button')}
        </Button>
      </header>

      {sessions.length === 0 ? (
        <Card>
          <p className="text-sm text-muted-foreground">{t('imports.empty')}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">{t('imports.list_filename')}</th>
                <th className="px-4 py-2 text-left">{t('imports.list_campaign')}</th>
                <th className="px-4 py-2 text-left">{t('imports.list_status')}</th>
                <th className="px-4 py-2 text-right">{t('imports.list_rows')}</th>
                <th className="px-4 py-2 text-left">{t('imports.list_provider')}</th>
                <th className="px-4 py-2 text-left">{t('imports.list_uploaded')}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const campaign = campaigns.find((c) => c.id === s.campaignId);
                return (
                  <tr key={s.id} className="border-t">
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/admin/imports/${s.id}`} className="text-primary hover:underline">
                        {s.filename}
                        {s.version > 1 && (
                          <span className="ml-1 text-xs text-muted-foreground">v{s.version}</span>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{campaign?.name ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          STATUS_BADGE[s.status] ?? 'bg-muted'
                        }`}
                      >
                        {t(`imports.detail_status_${s.status.toLowerCase()}`, s.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{s.rowCount ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {s.llmProvider}
                      {s.llmModel ? ` / ${s.llmModel}` : ''}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {showUpload && (
        <UploadModal
          campaigns={campaigns}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function UploadModal({
  campaigns,
  onClose,
  onUploaded,
}: {
  campaigns: CampaignSummary[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [campaignId, setCampaignId] = useState<string>('');
  const [provider, setProvider] = useState<{ provider: string | null; available: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<{ provider: string | null; available: boolean }>('/ai/status')
      .then(({ data }) => setProvider(data))
      .catch(() => setProvider({ provider: null, available: false }));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    if (campaignId) formData.append('campaignId', campaignId);
    try {
      const { data } = await apiClient.post<{ session: ImportSession }>(
        '/imports',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      onUploaded();
      navigate(`/admin/imports/${data.session.id}`);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? 'Upload failed');
      setSubmitting(false);
    }
  }

  const isLocalProvider = provider?.provider === 'OLLAMA' || provider?.provider === 'STUB';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('imports.modal_title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('imports.modal_file')}</label>
            <input
              type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
            <p className="text-xs text-muted-foreground">
              {t('imports.modal_file_hint')}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">{t('imports.modal_campaign')}</label>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('imports.modal_no_campaign')}</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {provider && provider.available && (
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              {isLocalProvider
                ? t('imports.modal_privacy_local')
                : t('imports.modal_privacy_remote', { provider: provider.provider ?? '?' })}
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting || !file}>
              {submitting ? t('imports.modal_uploading') : t('imports.modal_upload')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
